import type { WalletContextState } from '@solana/wallet-adapter-react';
import { Transaction } from '@solana/web3.js';
import { uniq } from 'es-toolkit/array';
import { delay } from 'es-toolkit/promise';
import { buildReleaseNftMetadata } from '../../../api/src/shared/nft/releaseMetadataBuilder';
import {
  asRecord,
  asStringArray,
  extractMediaDimensions,
  normalizeOptionalUrl,
  normalizeReleaseName,
} from '../../../api/src/shared/releaseMetadata/normalize';
import { NFT_SCHEMA_VERSION } from '../constants/nft';
import { R2_PUBLIC_HOSTS } from '../lib/config';
import { calculateFileHash, calculateJsonHash } from './crypto';
import { resolveFileInfo } from './fileResolution';
import { fetchAndConvertToFile } from './fetchAndConvertToFile';
import { getImageOnlyDimensions, getMediaDimensions } from './imageDimensions';
import {
  uploadArrayBufferWithProgress,
  uploadFileWithProgress,
} from './uploadHelpers';
import type { TRPC } from '../contexts/TRPC';
import {
  APK_EXTENSION_ERROR_MESSAGE,
  hasApkFileExtension,
} from './apkValidation';
import { buildReleaseDisplayName, buildReleaseNftName } from './releaseNftName';
import type { RightResult, RouterInputs, RouterOutputs } from '../types/TRPC';
import { isBlockhashStaleError, MAX_BLOCKHASH_RETRIES } from './blockhash';
import { getErrorMessage } from './errorHandling';
import { getSubmittedReleaseMint } from './releaseMinting';
import type { PublicationSession } from '@src/dto';

export type PublicationSource =
  | { kind: 'existingRelease'; sourceReleaseId: string }
  | {
      kind: 'portalUpload';
      file: File;
    }
  | {
      kind: 'externalUrl';
      url: string;
    };

export interface PublicationProgressState {
  phase:
    | 'preparing'
    | 'uploading'
    | 'queued'
    | 'processing'
    | 'minting'
    | 'verifying'
    | 'submitting'
    | 'done';
  progress: number;
  message: string;
  releaseId?: string;
  ingestionSessionId?: string;
  publicationSessionId?: string;
  transactionSignature?: string;
  mintAddress?: string;
}

export interface PublicationSubmissionResult {
  releaseId: string;
  ingestionSessionId?: string;
  publicationSessionId?: string;
  mintAddress?: string;
  transactionSignature?: string;
  hubspotTicketId?: string;
}

export interface PublicationSubmissionInput {
  trpc: TRPC['client'];
  wallet: WalletContextState;
  dappId: string;
  whatsNew: string;
  source: PublicationSource;
  onProgress?: (state: PublicationProgressState) => void;
  ingestionSessionId?: string;
}

type PortalResult<T> =
  | {
      _tag: 'Left';
      left: {
        message?: string;
        name?: string;
      };
    }
  | {
      _tag: 'Right';
      right: T;
    };

export type PortalClient = TRPC['client'];

type PublicationUploadTarget = RightResult<
  RouterOutputs['publication']['createUploadTarget']
>;
type CreatePublicationUploadTargetInput =
  RouterInputs['publication']['createUploadTarget'];
type CreatePublicationIngestionSessionInput =
  RouterInputs['publication']['createIngestionSession'];
type SigningWallet = WalletContextState & {
  publicKey: NonNullable<WalletContextState['publicKey']>;
  signTransaction: NonNullable<WalletContextState['signTransaction']>;
};

function unwrapPortalResult<T>(result: PortalResult<T>, fallback: string): T {
  if (result._tag === 'Left') {
    throw new Error(result.left.message || fallback);
  }

  return result.right;
}

async function finalizePublicationUpload(
  portal: PortalClient,
  uploadTarget: PublicationUploadTarget,
  input: CreatePublicationUploadTargetInput,
  fallback: string,
) {
  const stagingKey = firstString(uploadTarget, ['stagingKey']) || '';
  if (!stagingKey) {
    throw new Error('The portal did not return a staging upload key.');
  }

  return unwrapPortalResult(
    await portal.publication.finalizeUpload.mutate({
      ...input,
      stagingKey,
    }),
    fallback,
  );
}

async function signAndSubmitPreparedTransaction<T>({
  portal,
  wallet,
  publicationSessionId,
  prepare,
  getSerializedTransaction,
  prepareError,
  transactionMissingError,
  submitError,
}: {
  portal: PortalClient;
  wallet: SigningWallet;
  publicationSessionId: string;
  prepare: () => Promise<PortalResult<T>>;
  getSerializedTransaction: (prepared: T) => string | undefined;
  prepareError: string;
  transactionMissingError: string;
  submitError: string;
}): Promise<{ prepared: T; submission: unknown }> {
  for (let attempt = 1; attempt <= MAX_BLOCKHASH_RETRIES; attempt++) {
    try {
      const prepared = unwrapPortalResult(await prepare(), prepareError);
      const serializedTransaction = getSerializedTransaction(prepared);
      if (!serializedTransaction) {
        throw new Error(transactionMissingError);
      }

      const signedTransaction = await wallet.signTransaction(
        Transaction.from(decodeBase64(serializedTransaction)),
      );
      const submission = unwrapPortalResult(
        await portal.publication.submitSignedTransaction.mutate({
          signedTransaction: encodeBase64(signedTransaction.serialize()),
          publicationSessionId,
        }),
        submitError,
      );

      return { prepared, submission };
    } catch (error) {
      if (
        attempt < MAX_BLOCKHASH_RETRIES &&
        isBlockhashStaleError(getErrorMessage(error))
      ) {
        continue;
      }

      throw error;
    }
  }

  throw new Error(submitError);
}

function readDeep(value: unknown, path: string): unknown {
  const parts = path.split('.');
  let current: unknown = value;

  for (const part of parts) {
    const record = asRecord(current);
    if (!record) {
      return undefined;
    }

    current = record[part];
  }

  return current;
}

function inferFileExtension(fileName: string): string {
  const extension = fileName.split('.').pop()?.trim().toLowerCase() || 'apk';
  return extension.replace(/[^a-z0-9]/g, '') || 'apk';
}

function inferFileNameFromUrl(url: string): string {
  try {
    const pathname = new URL(url).pathname;
    const candidate = pathname.split('/').filter(Boolean).pop();
    return candidate || 'app-release.apk';
  } catch {
    return 'app-release.apk';
  }
}

function isR2PublicUrl(url: string): boolean {
  try {
    const hostname = new URL(url).hostname.toLowerCase();
    return R2_PUBLIC_HOSTS.includes(hostname);
  } catch {
    return false;
  }
}

function inferExtensionFromMimeType(mimeType: string): string | undefined {
  switch (mimeType) {
    case 'image/png':
      return 'png';
    case 'image/jpeg':
      return 'jpg';
    case 'image/webp':
      return 'webp';
    case 'image/gif':
      return 'gif';
    case 'image/svg+xml':
      return 'svg';
    case 'video/mp4':
      return 'mp4';
    case 'video/webm':
      return 'webm';
    case 'video/quicktime':
      return 'mov';
    case 'application/json':
      return 'json';
    default: {
      const subtype = mimeType
        .split('/')[1]
        ?.split('+')[0]
        ?.trim()
        .toLowerCase();
      return subtype?.replace(/[^a-z0-9]/g, '') || undefined;
    }
  }
}

function inferUploadFileExtension(fileName: string, mimeType: string): string {
  const byName = fileName.split('.').pop()?.trim().toLowerCase();
  if (byName && /^[a-z0-9]+$/.test(byName)) {
    return byName;
  }

  return inferExtensionFromMimeType(mimeType) || 'bin';
}

function firstString(value: unknown, paths: string[]): string | undefined {
  for (const path of paths) {
    const candidate = readDeep(value, path);
    if (typeof candidate === 'string' && candidate.length > 0) {
      return candidate;
    }
  }

  return undefined;
}

function decodeBase64(value: string): Uint8Array {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded =
    normalized.length % 4 === 0
      ? normalized
      : normalized.padEnd(
          normalized.length + (4 - (normalized.length % 4)),
          '=',
        );
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index++) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
}

function encodeBase64(value: Uint8Array): string {
  let binary = '';

  for (const byte of value) {
    binary += String.fromCharCode(byte);
  }

  return btoa(binary);
}

function isReadyStatus(status: string): boolean {
  const normalized = status.toLowerCase();
  return (
    normalized.includes('processed') ||
    normalized.includes('complete') ||
    normalized.includes('ready') ||
    normalized.includes('verified') ||
    normalized.includes('published')
  );
}

function isFailureStatus(status: string): boolean {
  const normalized = status.toLowerCase();
  return (
    normalized.includes('failed') ||
    normalized.includes('error') ||
    normalized.includes('rejected')
  );
}

type PublicationMediaPurpose =
  | 'icon'
  | 'screenshot'
  | 'banner'
  | 'featureGraphic';

type PortalFileMetadata = {
  fileHash?: string;
  fileSize?: number;
  metadata?: unknown;
  mimeType?: string;
};

function normalizeMimeType(value?: string | null): string | undefined {
  return value?.split(';')[0]?.trim().toLowerCase();
}

async function loadPortalFileMetadataMap(
  portal: PortalClient,
  uris: string[],
): Promise<Record<string, PortalFileMetadata>> {
  const uniqueUris = uniq(uris.filter((uri) => uri.length > 0));
  if (uniqueUris.length === 0) {
    return {};
  }

  try {
    const result = await portal.getFileMetadataByUris.query({
      uris: uniqueUris,
    });

    if (result._tag === 'Left') {
      console.warn(
        'Failed to load file metadata for release publication:',
        result.left.message,
      );
      return {};
    }

    const rawMetadata = asRecord(result.right) ?? {};
    const metadataMap: Record<string, PortalFileMetadata> = {};

    for (const [uri, value] of Object.entries(rawMetadata)) {
      const record = asRecord(value);
      if (!record) {
        continue;
      }

      metadataMap[uri] = {
        fileHash:
          typeof record.fileHash === 'string' ? record.fileHash : undefined,
        fileSize:
          typeof record.fileSize === 'number' ? record.fileSize : undefined,
        metadata: record.metadata,
        mimeType:
          typeof record.mimeType === 'string' ? record.mimeType : undefined,
      };
    }

    return metadataMap;
  } catch (error) {
    console.warn('Error loading release publication file metadata:', error);
    return {};
  }
}

async function resolveMediaItem(
  portal: PortalClient,
  metadataMap: Record<string, PortalFileMetadata>,
  input: {
    dappId?: string;
    defaultMimeType?: string;
    fallbackFileName: string;
    mode: 'image' | 'media';
    purpose: PublicationMediaPurpose;
    uri: string;
  },
) {
  const storedMetadata = metadataMap[input.uri];
  const storedDimensions = extractMediaDimensions(storedMetadata?.metadata);
  const storedMimeType = normalizeMimeType(storedMetadata?.mimeType);
  const isAlreadyPublicR2 = isR2PublicUrl(input.uri);

  if (
    isAlreadyPublicR2 &&
    storedMetadata?.fileHash &&
    storedDimensions &&
    (storedMimeType || input.defaultMimeType)
  ) {
    return {
      mime:
        storedMimeType || input.defaultMimeType || 'application/octet-stream',
      purpose: input.purpose,
      uri: input.uri,
      width: storedDimensions.width,
      height: storedDimensions.height,
      sha256: storedMetadata.fileHash,
    };
  }

  const resolvedFileInfo = resolveFileInfo(input.uri, input.fallbackFileName);
  const remoteFile = await fetchAndConvertToFile(input.uri, portal, {
    fileName: resolvedFileInfo.fileName,
    mimeType: storedMimeType || resolvedFileInfo.mimeType,
  });
  const resolvedMimeType =
    normalizeMimeType(remoteFile.type) ||
    storedMimeType ||
    resolvedFileInfo.mimeType ||
    input.defaultMimeType ||
    'application/octet-stream';
  const dimensions =
    storedDimensions ||
    (input.mode === 'image'
      ? await getImageOnlyDimensions(remoteFile)
      : await getMediaDimensions(remoteFile, resolvedMimeType));
  const fileHash = await calculateFileHash(remoteFile);

  let publicUri = input.uri;

  if (!isAlreadyPublicR2) {
    const uploadInput = {
      ...(input.dappId ? { dappId: input.dappId } : {}),
      fileHash,
      fileExtension: inferUploadFileExtension(
        remoteFile.name || resolvedFileInfo.fileName,
        resolvedMimeType,
      ),
      contentType: resolvedMimeType,
    };
    const uploadTarget = unwrapPortalResult(
      await portal.publication.createUploadTarget.mutate(uploadInput),
      `Failed to create an upload target for ${input.purpose}`,
    );

    const uploadUrl =
      firstString(uploadTarget, ['uploadUrl', 'url', 'signedUploadUrl']) || '';

    if (!uploadUrl) {
      throw new Error(
        `The portal did not return a valid upload target for ${input.purpose}.`,
      );
    }

    await uploadArrayBufferWithProgress(
      await remoteFile.arrayBuffer(),
      uploadUrl,
      undefined,
      resolvedMimeType,
    );

    const finalized = await finalizePublicationUpload(
      portal,
      uploadTarget,
      uploadInput,
      `Failed to finalize the ${input.purpose} upload`,
    );
    publicUri = firstString(finalized, ['publicUrl']) || '';

    if (!publicUri) {
      throw new Error(
        `The portal did not return a valid upload URL for ${input.purpose}.`,
      );
    }
  }

  return {
    mime: resolvedMimeType,
    purpose: input.purpose,
    uri: publicUri,
    width: dimensions.width,
    height: dimensions.height,
    sha256: fileHash,
  };
}

export function shouldReuseExistingReleaseMetadata(bundle: unknown): boolean {
  return Boolean(
    firstString(bundle, [
      'publicationSession.mintTransactionSignature',
      'publicationSession.verificationTransactionSignature',
    ]),
  );
}

export async function buildReleaseMetadataDocument(
  portal: PortalClient,
  bundle: unknown,
  source: PublicationSource,
) {
  const dappId = firstString(bundle, ['dapp.id']) || undefined;
  const releaseDisplayName = normalizeReleaseName(
    buildReleaseDisplayName({
      localizedName: firstString(bundle, ['release.localizedName']),
      dappName: firstString(bundle, ['dapp.dappName']),
    }),
  );
  const shortDescription =
    firstString(bundle, ['release.shortDescription', 'dapp.subtitle']) ||
    'Release NFT';
  const longDescription =
    firstString(bundle, ['release.longDescription', 'dapp.description']) ||
    shortDescription;
  const whatsNew = firstString(bundle, ['release.newInVersion']) || '';
  const publisherAddress =
    firstString(bundle, [
      'signerAuthority.dappWalletAddress',
      'signerAuthority.collectionAuthority',
      'dapp.walletAddress',
    ]) || '';
  const publisherName = firstString(bundle, ['publisher.name']) || '';
  const publisherWebsite = normalizeOptionalUrl(
    firstString(bundle, ['publisher.website', 'dapp.appWebsite']),
  );
  const publisherContact =
    firstString(bundle, [
      'publisher.email',
      'dapp.contactEmail',
      'dapp.supportEmail',
    ]) || '';
  const publisherSupportEmail =
    firstString(bundle, [
      'publisher.supportEmail',
      'dapp.supportEmail',
      'dapp.contactEmail',
      'publisher.email',
    ]) || publisherContact;
  const iconUri = firstString(bundle, ['dapp.dappIconUrl']) || '';
  const installUri =
    firstString(bundle, ['installFile.uri', 'release.releaseFileUrl']) ||
    (source.kind === 'externalUrl' ? source.url : '');
  const installMimeType =
    firstString(bundle, ['installFile.mimeType']) ||
    'application/vnd.android.package-archive';
  const installSize =
    Number(
      readDeep(bundle, 'installFile.size') ||
        readDeep(bundle, 'release.releaseFileSize') ||
        0,
    ) || 0;
  const installSha256 =
    firstString(bundle, ['installFile.sha256', 'release.releaseFileHash']) ||
    '';
  const androidPackage =
    firstString(bundle, ['release.androidPackage', 'dapp.androidPackage']) ||
    '';
  const versionName = firstString(bundle, ['release.versionName']) || '';
  const versionCode = Number(readDeep(bundle, 'release.versionCode') || 0) || 0;
  const minSdkVersion =
    Number(readDeep(bundle, 'release.minSdkVersion') || 1) || 1;
  const targetSdkRaw = readDeep(bundle, 'release.targetSdkVersion');
  const targetSdkVersion =
    typeof targetSdkRaw === 'number'
      ? targetSdkRaw
      : Number(targetSdkRaw || 0) || null;
  const certificateFingerprint =
    firstString(bundle, ['release.certificateFingerprint']) || '';
  const permissions = asStringArray(readDeep(bundle, 'release.permissions'));
  const locales =
    asStringArray(readDeep(bundle, 'release.locales')).length > 0
      ? asStringArray(readDeep(bundle, 'release.locales'))
      : asStringArray(readDeep(bundle, 'dapp.languages')).length > 0
        ? asStringArray(readDeep(bundle, 'dapp.languages'))
        : ['en-US'];
  const previewUris = asStringArray(readDeep(bundle, 'dapp.dappPreviewUrls'));
  const bannerUri = firstString(bundle, ['dapp.bannerUrl']);
  const featureGraphicUri =
    firstString(bundle, [
      'dapp.editorsChoiceGraphicUrl',
      'dapp.featureGraphicUrl',
    ]) || '';
  const mediaMetadataMap = await loadPortalFileMetadataMap(portal, [
    iconUri,
    ...previewUris,
    bannerUri || '',
    featureGraphicUri || '',
  ]);
  const media: Array<Record<string, unknown>> = [];
  let resolvedIconUri = iconUri;
  if (iconUri) {
    const iconMedia = await resolveMediaItem(portal, mediaMetadataMap, {
      dappId,
      defaultMimeType: 'image/png',
      fallbackFileName: 'release-icon.png',
      mode: 'image',
      purpose: 'icon',
      uri: iconUri,
    });
    resolvedIconUri = iconMedia.uri;
    media.push(iconMedia);
  }

  for (const [index, previewUri] of previewUris.entries()) {
    media.push(
      await resolveMediaItem(portal, mediaMetadataMap, {
        dappId,
        fallbackFileName: `release-screenshot-${index + 1}`,
        mode: 'media',
        purpose: 'screenshot',
        uri: previewUri,
      }),
    );
  }

  if (bannerUri) {
    media.push(
      await resolveMediaItem(portal, mediaMetadataMap, {
        dappId,
        defaultMimeType: 'image/png',
        fallbackFileName: 'release-banner.png',
        mode: 'image',
        purpose: 'banner',
        uri: bannerUri,
      }),
    );
  }

  if (featureGraphicUri) {
    media.push(
      await resolveMediaItem(portal, mediaMetadataMap, {
        dappId,
        defaultMimeType: 'image/png',
        fallbackFileName: 'release-feature-graphic.png',
        mode: 'image',
        purpose: 'featureGraphic',
        uri: featureGraphicUri,
      }),
    );
  }

  const licenseUrl = normalizeOptionalUrl(
    firstString(bundle, ['dapp.licenseUrl']),
  );
  const privacyPolicyUrl = normalizeOptionalUrl(
    firstString(bundle, ['dapp.privacyPolicyUrl']),
  );

  return buildReleaseNftMetadata({
    schemaVersion: NFT_SCHEMA_VERSION,
    name: releaseDisplayName,
    description: shortDescription,
    image: resolvedIconUri,
    externalUrl: publisherWebsite,
    creators: [
      {
        address: publisherAddress,
        share: 100,
      },
    ],
    publisherDetails: {
      name: publisherName,
      ...(publisherWebsite ? { website: publisherWebsite } : {}),
      contact: publisherContact,
      support_email: publisherSupportEmail,
    },
    releaseDetails: {
      updated_on: new Date().toISOString(),
      ...(licenseUrl ? { license_url: licenseUrl } : {}),
      copyright_url: '',
      ...(privacyPolicyUrl ? { privacy_policy_url: privacyPolicyUrl } : {}),
      localized_resources: {
        long_description: '1',
        new_in_version: '2',
        name: '4',
        short_description: '5',
      },
    },
    media,
    files: [
      {
        mime: installMimeType,
        purpose: 'install',
        size: installSize,
        sha256: installSha256,
        uri: installUri,
      },
    ],
    androidDetails: {
      android_package: androidPackage,
      version: versionName,
      version_code: versionCode,
      min_sdk: minSdkVersion,
      target_sdk: targetSdkVersion,
      cert_fingerprint: certificateFingerprint,
      permissions,
      locales,
    },
    i18n: {
      'en-US': {
        '1': longDescription,
        '2': whatsNew,
        '4': releaseDisplayName,
        '5': shortDescription.slice(0, 50),
      },
    },
  });
}

async function ensureReleaseMetadataUpload(
  portal: PortalClient,
  bundle: unknown,
  source: PublicationSource,
  onProgress?: (state: PublicationProgressState) => void,
  context?: {
    releaseId?: string;
    ingestionSessionId?: string;
    publicationSessionId?: string;
  },
) {
  const dappId = firstString(bundle, ['dapp.id']) || undefined;
  const existingMetadataUri = firstString(bundle, [
    'releaseMetadataUri',
    'release.nftMetadataUri',
    'publicationSession.metadataUri',
  ]);
  if (existingMetadataUri && shouldReuseExistingReleaseMetadata(bundle)) {
    return {
      metadataUri: existingMetadataUri,
    };
  }

  onProgress?.({
    phase: 'minting',
    progress: 45,
    message: existingMetadataUri
      ? 'Refreshing release metadata'
      : 'Preparing release metadata',
    releaseId: context?.releaseId,
    ingestionSessionId: context?.ingestionSessionId,
    publicationSessionId: context?.publicationSessionId,
  });

  const metadataDocument = await buildReleaseMetadataDocument(
    portal,
    bundle,
    source,
  );
  const metadataHash = await calculateJsonHash(metadataDocument);
  const uploadInput = {
    ...(dappId ? { dappId } : {}),
    fileHash: metadataHash,
    fileExtension: 'json',
    contentType: 'application/json',
  };
  const uploadTarget = unwrapPortalResult(
    await portal.publication.createUploadTarget.mutate(uploadInput),
    'Failed to create a release metadata upload target',
  );

  const uploadUrl =
    firstString(uploadTarget, ['uploadUrl', 'url', 'signedUploadUrl']) || '';

  if (!uploadUrl) {
    throw new Error(
      'The portal did not return a release metadata upload target.',
    );
  }

  const metadataBytes = new TextEncoder().encode(
    JSON.stringify(metadataDocument),
  );

  await uploadArrayBufferWithProgress(
    metadataBytes,
    uploadUrl,
    (progress) => {
      onProgress?.({
        phase: 'minting',
        progress: 50 + Math.round(progress * 0.05),
        message: 'Uploading release metadata',
        releaseId: context?.releaseId,
        ingestionSessionId: context?.ingestionSessionId,
        publicationSessionId: context?.publicationSessionId,
      });
    },
    'application/json',
  );

  const finalized = await finalizePublicationUpload(
    portal,
    uploadTarget,
    uploadInput,
    'Failed to finalize the release metadata upload',
  );
  const publicUrl = firstString(finalized, ['publicUrl']) || '';
  const uploadProviderId =
    firstString(finalized, ['providerId', 'id']) || undefined;

  if (!publicUrl) {
    throw new Error('The portal did not return a release metadata URL.');
  }

  return {
    metadataUri: publicUrl,
    uploadProviderId,
    uploadProvider: 'R2' as const,
  };
}

async function waitForIngestionSession(
  trpc: PortalClient,
  ingestionSessionId: string,
  onProgress?: (state: PublicationProgressState) => void,
) {
  const maxAttempts = 90;
  const delayMs = 2000;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const result = unwrapPortalResult(
      await trpc.publication.getIngestionSession.query({
        sessionId: ingestionSessionId,
      }),
      'Failed to fetch publication ingestion session',
    );

    const status =
      firstString(result, ['status', 'state', 'phase']) || 'Created';
    const releaseId =
      firstString(result, ['releaseId', 'release.id', 'bundle.release.id']) ||
      undefined;
    const publicationSessionId =
      firstString(result, ['publicationSessionId', 'publicationSession.id']) ||
      undefined;

    onProgress?.({
      phase: isReadyStatus(status)
        ? 'processing'
        : isFailureStatus(status)
          ? 'queued'
          : 'queued',
      progress: isReadyStatus(status)
        ? 50
        : Math.min(45, Math.round(((attempt + 1) / maxAttempts) * 45)),
      message: `Publication session ${status.toLowerCase()}`,
      releaseId,
      ingestionSessionId,
      publicationSessionId,
    });

    if (isReadyStatus(status)) {
      return result;
    }

    if (isFailureStatus(status)) {
      const message =
        firstString(result, ['error', 'message', 'processingError']) ||
        `Publication ingestion failed with status ${status}`;
      throw new Error(message);
    }

    await delay(delayMs);
  }

  throw new Error('Timed out waiting for the portal to process the release');
}

function ensureWalletSigning(
  wallet: WalletContextState,
): asserts wallet is SigningWallet {
  if (!wallet.publicKey || !wallet.signTransaction) {
    throw new Error(
      'Connect a wallet that can sign transactions before publishing.',
    );
  }
}

function resolvePublishContext(
  bundle: unknown,
  source: PublicationSource,
  publicationSession?: unknown,
) {
  const releaseId =
    firstString(bundle, ['release.id', 'publicationSession.releaseId']) ||
    firstString(publicationSession, ['releaseId']) ||
    undefined;
  const publicationSessionId =
    firstString(bundle, [
      'publicationSessionId',
      'publicationSession.id',
      'sessionId',
    ]) ||
    firstString(publicationSession, ['id']) ||
    undefined;
  const releaseVersion = firstString(bundle, ['release.versionName']) || '';
  const releaseName = buildReleaseNftName({
    localizedName: firstString(bundle, ['release.localizedName']),
    dappName: firstString(bundle, ['dapp.dappName']),
    versionName: releaseVersion,
  });
  const releaseMetadataUri =
    firstString(bundle, [
      'releaseMetadataUri',
      'release.nftMetadataUri',
      'publicationSession.metadataUri',
    ]) || '';
  const appMintAddress =
    firstString(bundle, [
      'signerAuthority.appMintAddress',
      'dapp.nftMintAddress',
    ]) || '';
  const publisherAddress =
    firstString(bundle, [
      'signerAuthority.dappWalletAddress',
      'signerAuthority.collectionAuthority',
      'dapp.walletAddress',
    ]) || '';
  const collectionAuthority =
    firstString(bundle, [
      'signerAuthority.collectionAuthority',
      'signerAuthority.dappWalletAddress',
      'dapp.walletAddress',
    ]) || '';
  const androidPackage = firstString(bundle, ['dapp.androidPackage']) || '';
  const releaseFileUrl =
    firstString(bundle, [
      'installFile.uri',
      'release.releaseFileUrl',
      'release.fileUrl',
    ]) || (source.kind === 'externalUrl' ? source.url : '');
  const releaseFileName =
    firstString(bundle, ['release.releaseFileName']) ||
    (source.kind === 'portalUpload'
      ? source.file.name
      : source.kind === 'externalUrl'
        ? inferFileNameFromUrl(source.url)
        : 'release.apk');
  const releaseFileSizeValue =
    readDeep(bundle, 'installFile.size') ||
    readDeep(bundle, 'release.releaseFileSize') ||
    (source.kind === 'portalUpload' ? source.file.size : undefined);
  const releaseFileSize =
    typeof releaseFileSizeValue === 'number'
      ? releaseFileSizeValue
      : Number(releaseFileSizeValue || 0);
  const releaseFileHash =
    firstString(bundle, ['installFile.sha256', 'release.releaseFileHash']) ||
    undefined;

  if (!releaseId) {
    throw new Error('The portal did not return a release identifier.');
  }

  if (!publicationSessionId) {
    throw new Error('The portal did not return a publication session.');
  }

  if (!releaseMetadataUri) {
    throw new Error('The portal did not provide a release metadata URI.');
  }

  if (!appMintAddress) {
    throw new Error('The portal did not provide the app mint address.');
  }

  if (!publisherAddress) {
    throw new Error('The portal did not provide the publisher wallet.');
  }

  if (!collectionAuthority) {
    throw new Error('The portal did not provide the collection authority.');
  }

  if (!releaseFileUrl) {
    throw new Error('The portal did not provide the install file URL.');
  }

  return {
    releaseId,
    publicationSessionId,
    releaseName: normalizeReleaseName(releaseName),
    releaseVersion,
    releaseMetadataUri,
    appMintAddress,
    publisherAddress,
    collectionAuthority,
    androidPackage,
    releaseFileUrl,
    releaseFileName,
    releaseFileSize,
    releaseFileHash,
  };
}

type PublicationContext = ReturnType<typeof resolvePublishContext>;

type ReleaseMetadataUpload = {
  metadataUri: string;
  uploadProvider?: 'R2';
  uploadProviderId?: string;
};

async function preparePortalUploadSource(
  portal: PortalClient,
  dappId: string,
  source: Extract<PublicationSource, { kind: 'portalUpload' }>,
  onProgress?: (state: PublicationProgressState) => void,
): Promise<PublicationUploadTarget> {
  const fileHash = await calculateFileHash(source.file, (progress) => {
    onProgress?.({
      phase: 'preparing',
      progress: 5 + Math.round(progress * 0.05),
      message: 'Hashing APK',
    });
  });
  const fileExtension = inferFileExtension(source.file.name);
  const uploadInput = {
    dappId,
    fileHash,
    fileExtension,
    contentType: source.file.type || 'application/vnd.android.package-archive',
  };

  const uploadTarget = unwrapPortalResult(
    await portal.publication.createUploadTarget.mutate(uploadInput),
    'Failed to create an upload target',
  );

  const uploadUrl =
    firstString(uploadTarget, ['uploadUrl', 'url', 'signedUploadUrl']) || '';

  if (!uploadUrl) {
    throw new Error('The portal did not return an upload URL.');
  }

  onProgress?.({
    phase: 'uploading',
    progress: 10,
    message: 'Uploading APK to the portal',
  });

  await uploadFileWithProgress(source.file, uploadUrl, (progress) => {
    onProgress?.({
      phase: 'uploading',
      progress: 10 + Math.round(progress * 0.3),
      message: 'Uploading APK to the portal',
    });
  });

  const finalized = await finalizePublicationUpload(
    portal,
    uploadTarget,
    uploadInput,
    'Failed to finalize the APK upload',
  );

  return {
    ...uploadTarget,
    ...finalized,
  };
}

function createPublicationIngestionSessionInput({
  dappId,
  whatsNew,
  idempotencyKey,
  source,
  uploadTarget,
}: {
  dappId: string;
  whatsNew: string;
  idempotencyKey: string;
  source: PublicationSource;
  uploadTarget?: PublicationUploadTarget;
}): CreatePublicationIngestionSessionInput {
  if (source.kind === 'existingRelease') {
    return { dappId, whatsNew, idempotencyKey, source };
  }
  return source.kind === 'externalUrl'
    ? {
        dappId,
        whatsNew,
        idempotencyKey,
        source: {
          kind: 'externalUrl',
          apkUrl: source.url,
          releaseFileName: inferFileNameFromUrl(source.url),
        },
      }
    : {
        dappId,
        whatsNew,
        idempotencyKey,
        source: {
          kind: 'portalUpload',
          releaseFileUrl: firstString(uploadTarget, ['publicUrl']) || '',
          releaseFileName: source.file.name,
          releaseFileSize: source.file.size,
        },
      };
}

async function loadProcessedPublicationBundle(
  portal: PortalClient,
  ingestionSessionId: string,
  onProgress?: (state: PublicationProgressState) => void,
) {
  const processedSession = await waitForIngestionSession(
    portal,
    ingestionSessionId,
    onProgress,
  );

  const publicationSessionId =
    firstString(processedSession, [
      'publicationSessionId',
      'publicationSession.id',
      'sessionId',
    ]) || undefined;
  const processingReleaseId =
    firstString(processedSession, ['releaseId', 'release.id']) || undefined;
  const publicationSession = publicationSessionId
    ? unwrapPortalResult(
        await portal.publication.getPublicationSession.query({
          publicationSessionId,
        }),
        'Failed to load publication session',
      )
    : processingReleaseId
      ? unwrapPortalResult(
          await portal.publication.getPublicationSession.query({
            releaseId: processingReleaseId,
          }),
          'Failed to load publication session',
        )
      : undefined;
  if (!publicationSession) {
    throw new Error('The portal did not return a publication session.');
  }
  const bundleReleaseId = processingReleaseId || undefined;
  const resolvedReleaseId =
    bundleReleaseId || firstString(publicationSession, ['releaseId']) || '';
  if (!resolvedReleaseId) {
    throw new Error('The portal did not return a release identifier.');
  }
  const bundleInput = {
    releaseId: resolvedReleaseId,
  };
  const bundle = unwrapPortalResult(
    await portal.publication.getPublicationBundle.query(bundleInput),
    'Failed to load publication bundle',
  );

  return {
    bundle,
    publicationSession,
    publicationSessionId,
    resolvedReleaseId,
  };
}

async function mintAndSaveReleaseNft({
  portal,
  wallet,
  publicationContext,
  metadataUpload,
  publicationSession,
  ingestionSessionId,
  onProgress,
}: {
  portal: PortalClient;
  wallet: SigningWallet;
  publicationContext: PublicationContext;
  metadataUpload: ReleaseMetadataUpload;
  publicationSession: PublicationSession;
  ingestionSessionId: string;
  onProgress?: (state: PublicationProgressState) => void;
}) {
  onProgress?.({
    phase: 'minting',
    progress: 55,
    message: 'Preparing the release NFT transaction',
    releaseId: publicationContext.releaseId,
    ingestionSessionId,
    publicationSessionId: publicationContext.publicationSessionId,
  });

  const submittedMint = getSubmittedReleaseMint(
    publicationSession,
    wallet.publicKey.toBase58(),
  );
  const { prepared: prepareReleaseResult, submission } = submittedMint
    ? { prepared: submittedMint, submission: submittedMint }
    : await signAndSubmitPreparedTransaction({
        portal,
        wallet,
        publicationSessionId: publicationContext.publicationSessionId,
        prepare: () =>
          portal.publication.prepareReleaseNftTransaction.mutate({
            releaseId: publicationContext.releaseId,
            releaseName: publicationContext.releaseName,
            releaseMetadataUri: publicationContext.releaseMetadataUri,
            appMintAddress: publicationContext.appMintAddress,
            publisherAddress: publicationContext.publisherAddress,
            payerAddress: wallet.publicKey.toBase58(),
          }),
        getSerializedTransaction: (prepared) =>
          firstString(prepared, [
            'transaction',
            'serializedTransaction',
            'transactionBase64',
          ]),
        prepareError: 'Failed to prepare the release NFT transaction',
        transactionMissingError:
          'The portal did not return a release transaction.',
        submitError: 'Failed to submit the release NFT transaction',
      });
  const releaseTransactionSubmission = asRecord(submission);

  const mintAddress =
    firstString(prepareReleaseResult, ['mintAddress', 'nftMintAddress']) ||
    firstString(releaseTransactionSubmission, ['mintAddress']) ||
    undefined;
  const transactionSignature =
    firstString(releaseTransactionSubmission, [
      'transactionSignature',
      'signature',
      'txSignature',
    ]) || undefined;

  if (!mintAddress || !transactionSignature) {
    throw new Error('The portal did not return the mint transaction result.');
  }

  onProgress?.({
    phase: 'verifying',
    progress: 70,
    message: 'Saving the release NFT metadata',
    releaseId: publicationContext.releaseId,
    ingestionSessionId,
    publicationSessionId: publicationContext.publicationSessionId,
    mintAddress,
    transactionSignature,
  });

  unwrapPortalResult(
    await portal.publication.saveReleaseNftData.mutate({
      releaseId: publicationContext.releaseId,
      mintAddress,
      transactionSignature,
      metadataUri:
        firstString(prepareReleaseResult, ['metadataUri']) ||
        publicationContext.releaseMetadataUri,
      releaseName: publicationContext.releaseName,
      releaseVersion: publicationContext.releaseVersion,
      androidPackage: publicationContext.androidPackage,
      appMintAddress: publicationContext.appMintAddress,
      ownerAddress: wallet.publicKey.toBase58(),
      ...(metadataUpload.uploadProviderId
        ? {
            uploadProvider: metadataUpload.uploadProvider,
            uploadProviderId: metadataUpload.uploadProviderId,
          }
        : {}),
    }),
    'Failed to save the release NFT data',
  );

  return {
    mintAddress,
    transactionSignature,
  };
}

async function verifyReleaseCollection({
  portal,
  wallet,
  dappId,
  publicationContext,
  ingestionSessionId,
  mintAddress,
  transactionSignature,
  onProgress,
}: {
  portal: PortalClient;
  wallet: SigningWallet;
  dappId: string;
  publicationContext: PublicationContext;
  ingestionSessionId: string;
  mintAddress: string;
  transactionSignature: string;
  onProgress?: (state: PublicationProgressState) => void;
}) {
  onProgress?.({
    phase: 'verifying',
    progress: 80,
    message: 'Verifying collection membership',
    releaseId: publicationContext.releaseId,
    ingestionSessionId,
    publicationSessionId: publicationContext.publicationSessionId,
    mintAddress,
    transactionSignature,
  });

  await signAndSubmitPreparedTransaction({
    portal,
    wallet,
    publicationSessionId: publicationContext.publicationSessionId,
    prepare: () =>
      portal.publication.prepareVerifyCollectionTransaction.mutate({
        dappId,
        nftMintAddress: mintAddress,
        collectionMintAddress: publicationContext.appMintAddress,
        collectionAuthority: publicationContext.collectionAuthority,
        payerAddress: wallet.publicKey.toBase58(),
      }),
    getSerializedTransaction: (prepared) =>
      firstString(prepared, [
        'transaction',
        'serializedTransaction',
        'transactionBase64',
      ]),
    prepareError: 'Failed to prepare the collection verification transaction',
    transactionMissingError:
      'The portal did not return a verification transaction.',
    submitError: 'Failed to submit the collection verification transaction',
  });

  unwrapPortalResult(
    await portal.publication.markReleaseCollectionAsVerified.mutate({
      releaseId: publicationContext.releaseId,
    }),
    'Failed to mark the release collection as verified',
  );
}

async function submitReleaseToStore({
  portal,
  publicationContext,
  whatsNew,
  ingestionSessionId,
  mintAddress,
  transactionSignature,
  onProgress,
}: {
  portal: PortalClient;
  publicationContext: PublicationContext;
  whatsNew: string;
  ingestionSessionId: string;
  mintAddress: string;
  transactionSignature: string;
  onProgress?: (state: PublicationProgressState) => void;
}): Promise<PublicationSubmissionResult> {
  onProgress?.({
    phase: 'submitting',
    progress: 92,
    message: 'Submitting release to the store',
    releaseId: publicationContext.releaseId,
    ingestionSessionId,
    publicationSessionId: publicationContext.publicationSessionId,
    mintAddress,
    transactionSignature,
  });

  const submissionResult = unwrapPortalResult(
    await portal.publication.submitToStore.mutate({
      releaseId: publicationContext.releaseId,
      whatsNew,
      criticalUpdate: false,
      testingInstructions: undefined,
      isResubmission: false,
    }),
    'Failed to submit the release to the store',
  );

  onProgress?.({
    phase: 'done',
    progress: 100,
    message: submissionResult.message || 'Publication completed',
    releaseId: publicationContext.releaseId,
    ingestionSessionId,
    publicationSessionId: publicationContext.publicationSessionId,
    mintAddress,
    transactionSignature,
  });

  return {
    releaseId: publicationContext.releaseId,
    ingestionSessionId,
    publicationSessionId: publicationContext.publicationSessionId,
    mintAddress,
    transactionSignature,
    hubspotTicketId: submissionResult.hubspotTicketId || undefined,
  };
}

export async function submitPublicationUpdate({
  trpc,
  wallet,
  dappId,
  whatsNew,
  source,
  onProgress,
  ingestionSessionId: preparedIngestionSessionId,
}: PublicationSubmissionInput): Promise<PublicationSubmissionResult> {
  if (
    source.kind === 'portalUpload' &&
    !hasApkFileExtension(source.file.name)
  ) {
    throw new Error(APK_EXTENSION_ERROR_MESSAGE);
  }

  ensureWalletSigning(wallet);

  const portal = trpc;
  const idempotencyKey =
    typeof crypto?.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  onProgress?.({
    phase: 'preparing',
    progress: 5,
    message: 'Preparing portal ingestion session',
  });

  const uploadTarget =
    source.kind === 'portalUpload'
      ? await preparePortalUploadSource(portal, dappId, source, onProgress)
      : undefined;
  const createIngestionSessionInput = createPublicationIngestionSessionInput({
    dappId,
    whatsNew,
    idempotencyKey,
    source,
    uploadTarget,
  });

  if (
    createIngestionSessionInput.source.kind === 'portalUpload' &&
    !createIngestionSessionInput.source.releaseFileUrl
  ) {
    throw new Error('The portal did not return a public upload URL.');
  }

  const ingestionSession = preparedIngestionSessionId
    ? { id: preparedIngestionSessionId }
    : unwrapPortalResult(
        await portal.publication.createIngestionSession.mutate(
          createIngestionSessionInput,
        ),
        'Failed to create publication ingestion session',
      );

  const ingestionSessionId =
    firstString(ingestionSession, [
      'id',
      'ingestionSessionId',
      'publicationSessionId',
    ]) || undefined;

  if (!ingestionSessionId) {
    throw new Error('The portal did not return an ingestion session ID.');
  }

  onProgress?.({
    phase: 'queued',
    progress: 45,
    message: 'Waiting for portal validation',
    ingestionSessionId,
  });

  const {
    bundle,
    publicationSession,
    publicationSessionId,
    resolvedReleaseId,
  } = await loadProcessedPublicationBundle(
    portal,
    ingestionSessionId,
    onProgress,
  );

  const metadataUpload = await ensureReleaseMetadataUpload(
    portal,
    { ...(asRecord(bundle) ?? {}), publicationSession },
    source,
    onProgress,
    {
      releaseId: resolvedReleaseId,
      ingestionSessionId,
      publicationSessionId:
        firstString(publicationSession, ['id']) || publicationSessionId,
    },
  );

  const publicationContext = resolvePublishContext(
    {
      ...(asRecord(bundle) ?? {}),
      publicationSession,
      releaseMetadataUri: metadataUpload.metadataUri,
    },
    source,
  );

  const { mintAddress, transactionSignature } = await mintAndSaveReleaseNft({
    portal,
    wallet,
    publicationContext,
    metadataUpload,
    publicationSession,
    ingestionSessionId,
    onProgress,
  });

  await verifyReleaseCollection({
    portal,
    wallet,
    dappId,
    publicationContext,
    ingestionSessionId,
    mintAddress,
    transactionSignature,
    onProgress,
  });

  return submitReleaseToStore({
    portal,
    publicationContext,
    whatsNew,
    ingestionSessionId,
    mintAddress,
    transactionSignature,
    onProgress,
  });
}
